'use client';

import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import {
    Form,
    FormControl,
    FormField,
    FormItem,
    FormLabel,
    FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Loading } from '@/components/ui/loading';
import { authClient, useSession } from '@/server/auth/client';
import { useMultiTapTrigger } from '@/hooks/use-multi-tap-trigger';
import { zodResolver } from '@hookform/resolvers/zod';
import { AlertTriangle } from 'lucide-react';
import Image from 'next/image';
import { useRouter, useSearchParams } from 'next/navigation';
import { useCallback, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

const loginSchema = z.object({
    email: z.string().email('Please enter a valid email address'),
    password: z.string().min(1, 'Password is required'),
});

type LoginFormValues = z.infer<typeof loginSchema>;

export default function LoginClient() {
    const [error, setError] = useState<string | null>(null);
    const [isGoogleLoading, setIsGoogleLoading] = useState(false);
    const [isMicrosoftLoading, setIsMicrosoftLoading] = useState(false);
    const router = useRouter();
    const searchParams = useSearchParams();
    const callbackUrl = searchParams.get('callbackUrl');
    const { data: session, isPending } = useSession();

    const [showEmailLogin, setShowEmailLogin] = useState(false);

    const toggleEmailLogin = useCallback(() => {
        setShowEmailLogin((prev) => !prev);
    }, []);

    useEffect(() => {
        const handleKeyDown = (event: KeyboardEvent) => {
            if (
                event.shiftKey &&
                (event.ctrlKey || event.metaKey) &&
                event.key === 'E'
            ) {
                event.preventDefault();
                toggleEmailLogin();
            }
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, []);

    const handleLogoTap = useMultiTapTrigger({
        requiredTaps: 5,
        intervalMs: 2000,
        onTrigger: toggleEmailLogin,
    });

    const form = useForm<LoginFormValues>({
        resolver: zodResolver(loginSchema),
        defaultValues: {
            email: '',
            password: '',
        },
    });
    const {
        formState: { isSubmitting },
    } = form;

    useEffect(() => {
        if (isPending) return;
        if (session?.user) {
            const destination = callbackUrl || '/';
            router.replace(destination);
        }
    }, [session, isPending, callbackUrl, router]);

    const handleSubmit = async (values: LoginFormValues) => {
        setError(null);

        try {
            const result = await authClient.signIn.email(values);

            if (result?.error) {
                setError(result.error.message || 'An unknown error occurred');
            } else {
                // The useEffect will handle the redirect
                router.refresh();
            }
        } catch {
            setError(
                'Failed to log in. Please check your credentials and try again.',
            );
        }
    };

    const handleGoogleSignIn = async () => {
        setIsGoogleLoading(true);
        setError(null);

        try {
            await authClient.signIn.social({
                provider: 'google',
            });
        } catch {
            setError('Failed to sign in with Google. Please try again.');
        } finally {
            setIsGoogleLoading(false);
        }
    };

    const handleMicrosoftSignIn = async () => {
        setIsMicrosoftLoading(true);
        setError(null);

        try {
            await authClient.signIn.social({
                provider: 'microsoft',
            });
        } catch {
            setError('Failed to sign in with Microsoft. Please try again.');
        } finally {
            setIsMicrosoftLoading(false);
        }
    };

    // Show loading state while checking session
    if (isPending) {
        return <Loading message="Checking authentication..." />;
    }

    // If user is already logged in, show a loading state (will redirect shortly)
    if (session?.user) {
        return <Loading message="Redirecting..." />;
    }

    return (
        <div className="flex min-h-screen flex-col items-center justify-center py-12 sm:px-6 lg:px-8">
            <div className="sm:mx-auto sm:w-full sm:max-w-md">
                <button
                    type="button"
                    className="mx-auto block"
                    onClick={handleLogoTap}
                    aria-label="Projex"
                >
                    <Image
                        className="mx-auto h-12 w-auto"
                        src="/maskable-icon-512.png"
                        alt=""
                        width={48}
                        height={48}
                    />
                </button>
                <div className="text-center">
                    <h1 className="mt-4 mb-1 text-2xl font-bold">
                        Sign In to your Projex Account
                    </h1>
                </div>
            </div>

            <div className="mt-0 w-full sm:mx-auto sm:max-w-md">
                <div className="px-4 py-8 sm:px-10">
                    {error && (
                        <Alert variant="destructive" className="mb-4">
                            <AlertTriangle className="h-4 w-4" />
                            <AlertDescription>{error}</AlertDescription>
                        </Alert>
                    )}
                    {showEmailLogin && (
                        <Form {...form}>
                            <form
                                onSubmit={form.handleSubmit(handleSubmit)}
                                className="space-y-6"
                            >
                                <FormField
                                    control={form.control}
                                    name="email"
                                    render={({ field }) => (
                                        <FormItem>
                                            <FormLabel>Email address</FormLabel>
                                            <FormControl>
                                                <Input
                                                    type="email"
                                                    autoComplete="email"
                                                    placeholder="john@example.com"
                                                    {...field}
                                                />
                                            </FormControl>
                                            <FormMessage />
                                        </FormItem>
                                    )}
                                />

                                <FormField
                                    control={form.control}
                                    name="password"
                                    render={({ field }) => (
                                        <FormItem>
                                            <FormLabel>Password</FormLabel>
                                            <FormControl>
                                                <Input
                                                    type="password"
                                                    autoComplete="current-password"
                                                    placeholder="••••••••"
                                                    {...field}
                                                />
                                            </FormControl>
                                            <FormMessage />
                                        </FormItem>
                                    )}
                                />

                                <div>
                                    <Button
                                        type="submit"
                                        className="w-full"
                                        disabled={
                                            isSubmitting ||
                                            isGoogleLoading ||
                                            isMicrosoftLoading
                                        }
                                    >
                                        {isSubmitting ? (
                                            <Loading />
                                        ) : (
                                            'Sign in with Email'
                                        )}
                                    </Button>
                                </div>
                            </form>
                        </Form>
                    )}

                    <div className="mt-6">
                        {showEmailLogin && (
                            <div className="relative">
                                <div className="absolute inset-0 flex items-center">
                                    <div className="w-full border-t border-gray-300" />
                                </div>
                                <div className="relative flex justify-center text-sm">
                                    <span className="bg-white px-2 text-gray-500">
                                        Or continue with
                                    </span>
                                </div>
                            </div>
                        )}

                        <div className="mt-6 space-y-3">
                            <Button
                                variant="outline"
                                className="w-full"
                                onClick={handleGoogleSignIn}
                                disabled={
                                    isSubmitting ||
                                    isGoogleLoading ||
                                    isMicrosoftLoading
                                }
                            >
                                {isGoogleLoading ? (
                                    <Loading />
                                ) : (
                                    <>
                                        <svg
                                            xmlns="http://www.w3.org/2000/svg"
                                            width="0.98em"
                                            height="1em"
                                            viewBox="0 0 256 262"
                                            className="mr-2"
                                        >
                                            <path
                                                fill="#4285f4"
                                                d="M255.878 133.451c0-10.734-.871-18.567-2.756-26.69H130.55v48.448h71.947c-1.45 12.04-9.283 30.172-26.69 42.356l-.244 1.622l38.755 30.023l2.685.268c24.659-22.774 38.875-56.282 38.875-96.027"
                                            ></path>
                                            <path
                                                fill="#34a853"
                                                d="M130.55 261.1c35.248 0 64.839-11.605 86.453-31.622l-41.196-31.913c-11.024 7.688-25.82 13.055-45.257 13.055c-34.523 0-63.824-22.773-74.269-54.25l-1.531.13l-40.298 31.187l-.527 1.465C35.393 231.798 79.49 261.1 130.55 261.1"
                                            ></path>
                                            <path
                                                fill="#fbbc05"
                                                d="M56.281 156.37c-2.756-8.123-4.351-16.827-4.351-25.82c0-8.994 1.595-17.697 4.206-25.82l-.073-1.73L15.26 71.312l-1.335.635C5.077 89.644 0 109.517 0 130.55s5.077 40.905 13.925 58.602z"
                                            ></path>
                                            <path
                                                fill="#eb4335"
                                                d="M130.55 50.479c24.514 0 41.05 10.589 50.479 19.438l36.844-35.974C195.245 12.91 165.798 0 130.55 0C79.49 0 35.393 29.301 13.925 71.947l42.211 32.783c10.59-31.477 39.891-54.251 74.414-54.251"
                                            ></path>
                                        </svg>
                                        Sign in with Google
                                    </>
                                )}
                            </Button>
                            <Button
                                variant="outline"
                                className="w-full"
                                onClick={handleMicrosoftSignIn}
                                disabled={
                                    isSubmitting ||
                                    isGoogleLoading ||
                                    isMicrosoftLoading
                                }
                            >
                                {isMicrosoftLoading ? (
                                    <Loading />
                                ) : (
                                    <>
                                        <svg
                                            xmlns="http://www.w3.org/2000/svg"
                                            width="1em"
                                            height="1em"
                                            viewBox="0 0 23 23"
                                            className="mr-2"
                                        >
                                            <path
                                                fill="#f35325"
                                                d="M1 1h10v10H1z"
                                            />
                                            <path
                                                fill="#81bc06"
                                                d="M12 1h10v10H12z"
                                            />
                                            <path
                                                fill="#05a6f0"
                                                d="M1 12h10v10H1z"
                                            />
                                            <path
                                                fill="#ffba08"
                                                d="M12 12h10v10H12z"
                                            />
                                        </svg>
                                        Sign in with Microsoft
                                    </>
                                )}
                            </Button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
